import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocalStorage, useSessionStorage } from "usehooks-ts";
import { hasRealUserMessages } from "../conversationMiniMap";
import { hasMcpToolUsage } from "../mcpUsageDetection";
import type { AgentChatMessage, AgentSessionState } from "../types";
import type { PaneResizeControl } from "../workspace-pane/PaneResizeHandle";
import { useDiffViewerEnabled } from "../workspace-pane/workspacePaneFlag";
import { usePanelResize } from "../workspace-pane/usePanelResize";
import { useLatestRef } from "@/hooks/useLatestRef";

/** A Codesmith Project's coordinator hosts the project panel in the right sidebar. */
export function isProjectCoordinatorSession(session: Pick<AgentSessionState, "mode" | "projectId"> | null): session is Pick<AgentSessionState, "mode" | "projectId"> & {
  projectId: string;
} {
  return session?.mode === "project_coordinator" && !!session.projectId;
}
export const MIN_RIGHT_SIDEBAR_WIDTH = 260;
const DEFAULT_MAX_RIGHT_SIDEBAR_WIDTH = 480;

/**
 * Width key/default are exported so the panel space budget (Codesmith.tsx)
 * can reason about the right panel's footprint without mounting it.
 */
export const RIGHT_SIDEBAR_WIDTH_KEY = "agentRightSidebarWidth";
export const DEFAULT_RIGHT_SIDEBAR_WIDTH = 320;

/**
 * Manual open while the container lacks space for the right panel. Session
 * storage (not component state) so the two consumers of the effective open
 * state, Codesmith's toggle-all shortcut and this hook inside
 * AgentSessionView, always resolve the same value. Exported so the loading
 * skeleton (skeletonLayout.ts) reserves the panel's footprint on reload.
 */
export const RIGHT_SIDEBAR_NARROW_OPEN_OVERRIDE_KEY = "agentRightSidebarOpenWhileNarrow";

/**
 * localStorage key for the user's preferred right-sidebar state. This is a
 * single global preference (not per-session): expanding or collapsing the
 * sidebar in one session carries over to every other session.
 */
export const RIGHT_SIDEBAR_OPEN_PREFERENCE_KEY = "agentRightSidebarOpen";

/**
 * Prefix of the retired per-session keys (`agentRightSidebarOpen:<id>`)
 * that older clients wrote. Migrated into the global preference and then
 * swept from localStorage on mount so stale entries don't accumulate
 * forever. The trailing colon keeps the sweep from matching the global
 * preference key itself.
 */
const LEGACY_PER_SESSION_KEY_PREFIX = `${RIGHT_SIDEBAR_OPEN_PREFERENCE_KEY}:`;
function sweepLegacyOpenPreferences(migrateExpanded: () => void) {
  try {
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(LEGACY_PER_SESSION_KEY_PREFIX)) stale.push(key);
    }
    if (window.localStorage.getItem(RIGHT_SIDEBAR_OPEN_PREFERENCE_KEY) === null && stale.some(key => window.localStorage.getItem(key) === "true")) {
      migrateExpanded();
    }
    stale.forEach(key => window.localStorage.removeItem(key));
  } catch {
    // Ignore disabled-storage errors; leftover keys are harmless.
  }
}

/**
 * The user's preferred right-sidebar state (expanded vs collapsed),
 * backed by a single localStorage key shared across all sessions.
 * Collapsed is the default for first-time users. Note this is the
 * preference only; whether the sidebar actually renders also depends on
 * it having content to show (see `useRightSidebar`).
 */
export function useRightSidebarOpenPreference() {
  const $ = _c(6);
  const [preference, setPreference] = useLocalStorage(RIGHT_SIDEBAR_OPEN_PREFERENCE_KEY, false);
  const setPreferenceRef = useLatestRef(setPreference);
  let t0;
  let t1;
  if ($[0] !== setPreferenceRef) {
    t0 = () => {
      sweepLegacyOpenPreferences(() => setPreferenceRef.current(true));
    };
    t1 = [setPreferenceRef];
    $[0] = setPreferenceRef;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== preference || $[4] !== setPreference) {
    t2 = [preference, setPreference];
    $[3] = preference;
    $[4] = setPreference;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2 as const;
}

/**
 * The stored open preference, made space-aware: with room for the panel the
 * preference is the source of truth; without room the panel starts closed
 * regardless, and a manual open is tracked separately so a cramped window
 * never rewrites the wide-screen preference. Losing space (window shrink,
 * left sidebar opening) discards the manual open.
 */
export function useSpaceAwareRightSidebarPreference(hasSpaceForSidebar) {
  const $ = _c(11);
  const [preference, setPreference] = useRightSidebarOpenPreference();
  const [openedWhileNarrow, setOpenedWhileNarrow] = useSessionStorage(RIGHT_SIDEBAR_NARROW_OPEN_OVERRIDE_KEY, false);
  const prevHasSpaceRef = useRef(hasSpaceForSidebar);
  let t0;
  let t1;
  if ($[0] !== hasSpaceForSidebar || $[1] !== setOpenedWhileNarrow) {
    t0 = () => {
      if (prevHasSpaceRef.current && !hasSpaceForSidebar) {
        setOpenedWhileNarrow(false);
      }
      prevHasSpaceRef.current = hasSpaceForSidebar;
    };
    t1 = [hasSpaceForSidebar, setOpenedWhileNarrow];
    $[0] = hasSpaceForSidebar;
    $[1] = setOpenedWhileNarrow;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  const open = hasSpaceForSidebar ? preference : openedWhileNarrow;
  let t2;
  if ($[4] !== hasSpaceForSidebar || $[5] !== setOpenedWhileNarrow || $[6] !== setPreference) {
    t2 = value => {
      if (hasSpaceForSidebar) {
        setPreference(value);
        return;
      }
      setOpenedWhileNarrow(value);
    };
    $[4] = hasSpaceForSidebar;
    $[5] = setOpenedWhileNarrow;
    $[6] = setPreference;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const setOpen = t2;
  let t3;
  if ($[8] !== open || $[9] !== setOpen) {
    t3 = [open, setOpen];
    $[8] = open;
    $[9] = setOpen;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3 as const;
}
function getMaxRightSidebarWidth() {
  return typeof window !== "undefined" ? Math.floor(window.innerWidth / 2) : DEFAULT_MAX_RIGHT_SIDEBAR_WIDTH;
}

/**
 * Whether a session has anything for the right sidebar to show (a
 * conversation mini-map, PRs, file attachments, a Linear issue, an
 * automation, or MCP tool usage). The stored open preference
 * only renders as a visible sidebar when this is true, so callers outside
 * `useRightSidebar` (e.g. the ⌘/ toggle-all shortcut) use this to reason
 * about the sidebar's effective visibility.
 */
export function sessionHasRightSidebarContent(session: AgentSessionState | null, activeMessages: AgentChatMessage[] = []): boolean {
  if (isProjectCoordinatorSession(session)) return true;
  const hasConversation = hasRealUserMessages(activeMessages);
  const hasPrs = session?.managedPrs && session.managedPrs.length > 0;
  const hasFiles = session?.chatMessages?.some(m => m.attachments && m.attachments.length > 0);
  const hasLinearIssue = !!session?.initialContext?.linearIssue?.issueUrl;
  const hasAutomation = !!session?.initialContext?.automation?.automationId;
  if (hasConversation || hasPrs || hasFiles || hasLinearIssue || hasAutomation) return true;
  return !!session?.chatMessages && hasMcpToolUsage(session.chatMessages);
}
export function useRightSidebar(session, t0, t1) {
  const $ = _c(37);
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const activeMessages = t2;
  const hasSpaceForSidebar = t1 === undefined ? true : t1;
  const [rightSidebarPreference, setRightSidebarPreference] = useSpaceAwareRightSidebarPreference(hasSpaceForSidebar);
  const [storedRightSidebarWidth, setStoredRightSidebarWidth] = useLocalStorage(RIGHT_SIDEBAR_WIDTH_KEY, DEFAULT_RIGHT_SIDEBAR_WIDTH);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const hasSession = session !== null;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const element = containerRef.current;
      if (!element) {
        return;
      }
      setContainerWidth(element.getBoundingClientRect().width);
      const observer = new ResizeObserver(t4 => {
        const [entry] = t4;
        setContainerWidth(entry.contentRect.width);
      });
      observer.observe(element);
      return () => observer.disconnect();
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== hasSession) {
    t4 = [hasSession];
    $[3] = hasSession;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useLayoutEffect(t3, t4);
  const summaryPanelEnabled = !useDiffViewerEnabled();
  let t5;
  if ($[5] !== activeMessages || $[6] !== session || $[7] !== summaryPanelEnabled) {
    t5 = summaryPanelEnabled && sessionHasRightSidebarContent(session, activeMessages);
    $[5] = activeMessages;
    $[6] = session;
    $[7] = summaryPanelEnabled;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const hasRightSidebarContent = t5;
  const rightSidebarOpen = rightSidebarPreference && hasRightSidebarContent;
  const rightSidebarOpenRef = useLatestRef(rightSidebarOpen);
  let t6;
  if ($[9] !== rightSidebarOpenRef || $[10] !== setRightSidebarPreference) {
    t6 = value => {
      const next = typeof value === "function" ? value(rightSidebarOpenRef.current) : value;
      setRightSidebarPreference(next);
    };
    $[9] = rightSidebarOpenRef;
    $[10] = setRightSidebarPreference;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const setRightSidebarOpen = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = getMaxRightSidebarWidth();
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== containerWidth) {
    t8 = containerWidth === null ? getMaxRightSidebarWidth() : Math.floor(containerWidth * 0.5);
    $[13] = containerWidth;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const rightSidebarMaxWidth = Math.min(t7, t8);
  let t9;
  if ($[15] !== storedRightSidebarWidth) {
    t9 = clientX => {
      const right = containerRef.current?.getBoundingClientRect().right;
      return right === undefined ? storedRightSidebarWidth : right - clientX;
    };
    $[15] = storedRightSidebarWidth;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const widthFromClientX = t9;
  let t10;
  if ($[17] !== rightSidebarMaxWidth || $[18] !== setStoredRightSidebarWidth || $[19] !== storedRightSidebarWidth || $[20] !== widthFromClientX) {
    t10 = {
      value: storedRightSidebarWidth,
      min: MIN_RIGHT_SIDEBAR_WIDTH,
      max: rightSidebarMaxWidth,
      widthFromClientX,
      onCommit: setStoredRightSidebarWidth
    };
    $[17] = rightSidebarMaxWidth;
    $[18] = setStoredRightSidebarWidth;
    $[19] = storedRightSidebarWidth;
    $[20] = widthFromClientX;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const resize = usePanelResize(t10);
  let t11;
  if ($[22] !== resize.isResizing || $[23] !== resize.onPointerCancel || $[24] !== resize.onPointerDown || $[25] !== resize.onPointerMove || $[26] !== resize.onPointerUp || $[27] !== resize.setValue || $[28] !== resize.value || $[29] !== rightSidebarMaxWidth) {
    t11 = {
      value: resize.value,
      min: MIN_RIGHT_SIDEBAR_WIDTH,
      max: rightSidebarMaxWidth,
      isResizing: resize.isResizing,
      onPointerDown: resize.onPointerDown,
      onPointerMove: resize.onPointerMove,
      onPointerUp: resize.onPointerUp,
      onPointerCancel: resize.onPointerCancel,
      onValueChange: resize.setValue
    };
    $[22] = resize.isResizing;
    $[23] = resize.onPointerCancel;
    $[24] = resize.onPointerDown;
    $[25] = resize.onPointerMove;
    $[26] = resize.onPointerUp;
    $[27] = resize.setValue;
    $[28] = resize.value;
    $[29] = rightSidebarMaxWidth;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const rightSidebarResize = t11;
  let t12;
  if ($[31] !== hasRightSidebarContent || $[32] !== resize.value || $[33] !== rightSidebarOpen || $[34] !== rightSidebarResize || $[35] !== setRightSidebarOpen) {
    t12 = {
      rightSidebarOpen,
      setRightSidebarOpen,
      rightSidebarWidth: resize.value,
      rightSidebarResize,
      containerRef,
      hasRightSidebarContent
    };
    $[31] = hasRightSidebarContent;
    $[32] = resize.value;
    $[33] = rightSidebarOpen;
    $[34] = rightSidebarResize;
    $[35] = setRightSidebarOpen;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
}